import { useContext } from 'react';
import PropTypes from 'prop-types';
import { css } from '@emotion/react';
import { Radio, Checkbox } from '@lego/klik-react';
import { spacing, font, colors } from '../../config';
import {
  SurveyContext,
  updateAnswerAction,
  updateSubAnswerAction,
} from '../survey/reducer';
import { computeScore } from '../survey/helpers';
import { AnswerLabel } from './AnswerLabel';
import { useLabels } from '../../providers/LabelsProvider';

const subAnswerWrapperStyle = css({
  marginTop: spacing.size4,
  marginLeft: spacing.size8,
});

const subAnswerHeaderStyle = css({
  fontSize: font.size.size05,
  fontWeight: font.weight.regular,
  whiteSpace: 'pre-wrap',
  marginTop: 0,
  marginBottom: spacing.size4,
  marginLeft: 0,
  color: 'inherit',
});

const subAnswerItemStyle = css({
  marginTop: spacing.size4,
});

const subAnswerWarningStyle = css({
  fontSize: font.size.sizeM1,
  backgroundColor: colors.utilityColors.warning.warning100,
  padding: `${spacing.size1}px ${spacing.size2}px`,
  marginBottom: spacing.size4,
  marginLeft: 0,
  color: colors.neutralColors.black,
});

const SingleChoice = ({ question, questionState }) => {
  const { dispatch } = useContext(SurveyContext);
  const {
    labels: { svy_sub_answers_reduced_discount },
  } = useLabels();
  const { questionId, questionType, answerOptions } = question;

  const mainAnswerIds = answerOptions.map((o) => o.answerId);
  const selectedSubIds = questionState.selectedAnswer.filter(
    (id) => !mainAnswerIds.includes(id)
  );

  return (
    <div>
      {answerOptions.map((answer) => {
        const isSelected = questionState.selectedAnswer.includes(
          answer.answerId
        );

        const displayScore =
          isSelected && answer.subAnswerOptions?.length > 0
            ? computeScore(answerOptions, answer.answerId, selectedSubIds)
            : answer.score;

        // Score is reduced when computeScore returns less than the answer's full score.
        const scoreWarning =
          isSelected &&
          answer.subAnswerOptions?.length > 0 &&
          displayScore !== answer.score;

        return (
          <div key={answer.answerId}>
            <Radio
              name={questionId}
              onChange={(event) =>
                dispatch(
                  updateAnswerAction(
                    questionId,
                    questionType,
                    event.target.value
                  )
                )
              }
            >
              <Radio.Button
                checked={isSelected ? 'checked' : false}
                label={
                  <AnswerLabel
                    labelText={answer.text}
                    score={displayScore}
                    scoreWarning={scoreWarning}
                  />
                }
                value={answer.answerId}
              />
            </Radio>
            {isSelected && answer.subAnswerOptions?.length > 0 && (
              <div css={subAnswerWrapperStyle}>
                {answer.subAnswerHeader && (
                  <div css={subAnswerHeaderStyle}>{answer.subAnswerHeader}</div>
                )}
                {scoreWarning && (
                  <div css={subAnswerWarningStyle}>
                    {svy_sub_answers_reduced_discount}
                  </div>
                )}
                {answer.subAnswerOptions.map((subOption) => (
                  <div key={subOption.answerId} css={subAnswerItemStyle}>
                    <Checkbox
                      checked={questionState.selectedAnswer.includes(
                        subOption.answerId
                      )}
                      onChange={() =>
                        dispatch(
                          updateSubAnswerAction(questionId, subOption.answerId)
                        )
                      }
                      label={subOption.text}
                      value={subOption.answerId}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

SingleChoice.propTypes = {
  question: PropTypes.object.isRequired,
  questionState: PropTypes.object.isRequired,
};

export { SingleChoice };
